// client/src/pages/PropertiesPage.js — the public Properties search (formerly "Listings").
// Filters live in the URL, so a search can be bookmarked, shared and saved. Results come
// from /api/public/properties (lib/listingSources): our own listings and, when the MLS
// feed is on, MLS listings with their attribution and the MLS disclaimer.
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { IdentityLine, Attribution, Disclaimer } from '../components/public/BrokerageIdentity';
import { addressLine, placeLine, factsLine, STATUS_STYLE, photoUrl, onImgError, paramsToFilters, filtersToQuery, portalToken, portalFetch } from '../utils/properties';
import { VirtualStagingBadge } from '../components/shared/VirtualStagingBadge';

const COMMERCIAL = ['commercial', 'industrial'];
const inputCls = 'mt-1 block w-full rounded-md border border-slate-400 bg-white px-3 py-2 text-slate-900 focus:border-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-600';
const labelCls = 'block text-sm font-medium text-slate-800';

export const PropertyCard = ({ l, identity, headingLevel = 'h2', action = null, badge = null }) => {
    const H = headingLevel;
    return (
        <article className="flex h-full flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <Link to={l.path} className="relative block focus:outline-none focus:ring-4 focus:ring-slate-600" aria-label={`${addressLine(l)}, ${placeLine(l)} — ${l.price_label}`}>
                <img src={photoUrl(l)} onError={onImgError} alt={l.photos && l.photos[0] && l.photos[0].caption ? l.photos[0].caption : `Photo of ${addressLine(l)}`}
                    className="h-52 w-full object-cover" loading="lazy" />
                {l.photos && l.photos[0] && l.photos[0].virtually_staged && <VirtualStagingBadge />}
            </Link>
            <div className="flex flex-grow flex-col p-4">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                    <span className={`rounded px-2 py-0.5 text-xs font-semibold ${STATUS_STYLE[l.status] || 'bg-slate-100 text-slate-800'}`}>{l.status_label}</span>
                    <span className="text-xs font-medium text-slate-700">{l.type_label}</span>
                    {l.source === 'own' && <span className="rounded bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-900">{badge || 'Our listing'}</span>}
                </div>
                <p className="text-xl font-bold text-slate-900">{l.price_label}</p>
                <H className="mt-1 text-base font-semibold text-slate-900">
                    <Link to={l.path} className="hover:text-indigo-700 hover:underline">{addressLine(l)}</Link>
                </H>
                <p className="text-sm text-slate-700">{placeLine(l)}</p>
                {factsLine(l) && <p className="mt-1 text-sm text-slate-700">{factsLine(l)}</p>}
                <div className="mt-auto pt-3">
                    {action}
                    <Attribution listing={l} className="mt-2" />
                    {l.source === 'own' && <IdentityLine identity={identity} className="mt-2" />}
                </div>
            </div>
        </article>
    );
};

const PropertiesPage = () => {
    const [sp, setSp] = useSearchParams();
    const [config, setConfig] = useState(null);
    const [badge, setBadge] = useState(null);
    const [ownersOpen, setOwnersOpen] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [form, setForm] = useState(() => paramsToFilters(sp));
    const [saveMsg, setSaveMsg] = useState('');
    const query = sp.toString();

    useEffect(() => { document.title = 'Properties for sale and lease | Business Real Estate Corp.'; }, []);
    useEffect(() => {
        fetch('/api/public/properties/config').then((r) => r.json()).then(setConfig).catch(() => setConfig(null));
        // "Listed with BREC" on the brokerage's own listings (owner listings settings).
        fetch('/api/public/owner-listings/config').then((r) => (r.ok ? r.json() : null)).then((c) => { setBadge(c && c.badge_text); setOwnersOpen(!!(c && c.enabled)); }).catch(() => {});
    }, []);
    useEffect(() => {
        setLoading(true);
        setError('');
        setForm(paramsToFilters(new URLSearchParams(query)));
        fetch(`/api/public/properties?${query}`)
            .then(async (r) => { const j = await r.json(); if (!r.ok) throw new Error(j.message); return j; })
            .then(setData)
            .catch((e) => setError(e.message || 'The property search is not available right now.'))
            .finally(() => setLoading(false));
    }, [query]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const submit = (e) => {
        e.preventDefault();
        const f = { ...form };
        delete f.page;
        setSp(filtersToQuery(f));
        setSaveMsg('');
    };
    const goPage = useCallback((p) => {
        const f = paramsToFilters(sp);
        f.page = String(p);
        setSp(filtersToQuery(f));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [sp, setSp]);
    const clear = () => { setForm({}); setSp(''); };

    const saveSearch = async () => {
        setSaveMsg('');
        try {
            await portalFetch('/api/client-portal/properties/saved-searches', { method: 'POST', body: JSON.stringify({ criteria: paramsToFilters(sp) }) });
            setSaveMsg('Saved. New matches will appear in your account under My Properties.');
        } catch (e) { setSaveMsg(e.message); }
    };

    const showCommercial = !form.types || COMMERCIAL.includes(form.types);
    const signedIn = !!portalToken();

    return (
        <div className="container mx-auto px-4 py-10 sm:px-6">
            <h1 className="text-3xl font-bold text-slate-900 sm:text-4xl">Properties</h1>
            {ownersOpen && (
                <div className="mt-4 flex flex-col items-start gap-3 rounded-lg border-2 border-amber-500 bg-amber-50 p-5 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xl font-bold text-slate-900 sm:text-2xl">Own a property? List it with us — for sale or for lease.</p>
                    <Link to="/list-your-property" className="whitespace-nowrap rounded-md bg-amber-400 px-6 py-3 text-lg font-extrabold uppercase tracking-wide text-slate-900 shadow-md ring-2 ring-amber-500 hover:bg-amber-300 sm:text-xl">
                        List Your Property
                    </Link>
                </div>
            )}
            <p className="mt-2 text-slate-700">Homes, condos, multi-family, land, commercial and industrial property for sale and lease.</p>

            <form onSubmit={submit} role="search" aria-label="Property search" className="mt-6 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="sm:col-span-2">
                        <label htmlFor="ps-location" className={labelCls}>Location</label>
                        <input id="ps-location" className={inputCls} value={form.location || ''} onChange={set('location')} placeholder="City, ZIP or county (e.g. Cook County)" autoComplete="off" />
                    </div>
                    <div>
                        <label htmlFor="ps-type" className={labelCls}>Property type</label>
                        <select id="ps-type" className={inputCls} value={form.types || ''} onChange={set('types')}>
                            <option value="">All types</option>
                            {(config ? config.types : []).map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="ps-status" className={labelCls}>Status</label>
                        <select id="ps-status" className={inputCls} value={form.status || ''} onChange={set('status')}>
                            <option value="">For sale or lease</option>
                            {(config ? config.statuses : []).map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="ps-min-price" className={labelCls}>Min price ($)</label>
                        <input id="ps-min-price" className={inputCls} inputMode="numeric" value={form.min_price || ''} onChange={set('min_price')} />
                    </div>
                    <div>
                        <label htmlFor="ps-max-price" className={labelCls}>Max price ($)</label>
                        <input id="ps-max-price" className={inputCls} inputMode="numeric" value={form.max_price || ''} onChange={set('max_price')} />
                    </div>
                    <div>
                        <label htmlFor="ps-beds" className={labelCls}>Beds (min)</label>
                        <select id="ps-beds" className={inputCls} value={form.min_beds || ''} onChange={set('min_beds')}>
                            <option value="">Any</option>{[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}+</option>)}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="ps-baths" className={labelCls}>Baths (min)</label>
                        <select id="ps-baths" className={inputCls} value={form.min_baths || ''} onChange={set('min_baths')}>
                            <option value="">Any</option>{[1, 1.5, 2, 3, 4].map((n) => <option key={n} value={n}>{n}+</option>)}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="ps-min-sqft" className={labelCls}>Min sq ft</label>
                        <input id="ps-min-sqft" className={inputCls} inputMode="numeric" value={form.min_sqft || ''} onChange={set('min_sqft')} />
                    </div>
                    <div>
                        <label htmlFor="ps-max-sqft" className={labelCls}>Max sq ft</label>
                        <input id="ps-max-sqft" className={inputCls} inputMode="numeric" value={form.max_sqft || ''} onChange={set('max_sqft')} />
                    </div>
                    {showCommercial && (
                        <>
                            <div>
                                <label htmlFor="ps-zoning" className={labelCls}>Zoning (commercial)</label>
                                <input id="ps-zoning" className={inputCls} value={form.zoning || ''} onChange={set('zoning')} placeholder="e.g. B3, M1, PMD" />
                            </div>
                            <div>
                                <label htmlFor="ps-cap" className={labelCls}>Min cap rate (%)</label>
                                <input id="ps-cap" className={inputCls} inputMode="decimal" value={form.min_cap_rate || ''} onChange={set('min_cap_rate')} />
                            </div>
                        </>
                    )}
                    <div>
                        <label htmlFor="ps-sort" className={labelCls}>Sort by</label>
                        <select id="ps-sort" className={inputCls} value={form.sort || 'featured'} onChange={set('sort')}>
                            {(config ? config.sorts : [{ key: 'featured', label: 'Our listings first' }]).map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                        </select>
                    </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-3">
                    <button type="submit" className="rounded-md bg-slate-900 px-5 py-2 font-semibold text-white hover:bg-slate-900 focus:outline-none focus:ring-4 focus:ring-indigo-300">Search</button>
                    <button type="button" onClick={clear} className="rounded-md border border-slate-400 px-5 py-2 font-semibold text-slate-800 hover:bg-slate-100 focus:outline-none focus:ring-4 focus:ring-indigo-300">Clear</button>
                    {signedIn
                        ? <button type="button" onClick={saveSearch} className="rounded-md border border-slate-800 px-5 py-2 font-semibold text-indigo-800 hover:bg-indigo-50 focus:outline-none focus:ring-4 focus:ring-indigo-300">Save this search</button>
                        : <Link to={`/client-portal/register?next=${encodeURIComponent(`/properties?${query}`)}`} className="rounded-md px-2 py-2 font-semibold text-indigo-800 underline">Sign up free to save searches and favourites</Link>}
                </div>
                <p className="mt-2 text-sm text-slate-700" role="status">{saveMsg}</p>
            </form>

            <section aria-labelledby="ps-results" className="mt-8">
                <h2 id="ps-results" className="sr-only">Search results</h2>
                <p className="mb-4 text-slate-800" aria-live="polite">
                    {loading ? 'Searching…' : error ? '' : data ? `${data.total.toLocaleString('en-US')} ${data.total === 1 ? 'property' : 'properties'} — ${data.description}` : ''}
                </p>
                {error && <p className="text-red-700" role="alert">{error}</p>}
                {!loading && data && data.total === 0 && (
                    <p className="rounded-lg bg-white p-6 text-slate-700">No properties match that search. Try a wider area or fewer filters — or <Link className="underline" to="/contact">tell us what you need</Link> and an agent will look for you.</p>
                )}
                {data && (
                    <ul className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {data.listings.map((l) => <li key={l.key}><PropertyCard l={l} identity={data.identity} headingLevel="h3" badge={badge} /></li>)}
                    </ul>
                )}
                {data && data.pages > 1 && (
                    <nav aria-label="Result pages" className="mt-8 flex items-center justify-center gap-3">
                        <button type="button" disabled={data.page <= 1} onClick={() => goPage(data.page - 1)}
                            className="rounded-md border border-slate-400 px-4 py-2 font-medium text-slate-800 disabled:opacity-40">Previous</button>
                        <span className="text-slate-800" aria-current="page">Page {data.page} of {data.pages}</span>
                        <button type="button" disabled={data.page >= data.pages} onClick={() => goPage(data.page + 1)}
                            className="rounded-md border border-slate-400 px-4 py-2 font-medium text-slate-800 disabled:opacity-40">Next</button>
                    </nav>
                )}
                {data && <IdentityLine identity={data.identity} className="mt-8 text-center" />}
                {data && <Disclaimer text={data.disclaimer} />}
            </section>
        </div>
    );
};

export default PropertiesPage;
